// Migrated
<template>
  <div class="info-carousel block">
    <div
      ref="mainImage"
      class="info-carousel-main relative mb-1 md:mb-2"
    >
      <Swiper
        class="size-full"
        arrows-align="border"
        loop
        v-bind="mainCarouselProps"
        :loading-skeleton-aspect="false"
        @slide-change="handleSlideChange"
      >
        <div
          v-for="(image, index) in getImages"
          :key="`slide_${index}`"
          class="flex justify-center size-full"
        >
          <ResponsiveHotelImage
            class="size-full pointer-events-none"
            :image="image"
            :alt="`Slide ${index + 1}`"
            fit="cover"
            clip="height"
            :sizing-ref="$refs.mainImage"
          />
        </div>
      </Swiper>
      <div
        v-if="images.length > 1"
        class="absolute bg-dark-blue text-white right-0 top-0 px-4 py-1 z-[2]"
      >
        + {{ $t('images', images.length - 1) }}
      </div>
      <button
        v-if="canFullscreen"
        class="absolute bg-transparent bottom-0 p-0 right-0 mb-4 mr-4 text-white opacity-75 z-[2] hover:opacity-100"
        @click="showFullscreenImageCarousel"
      >
        <i-expand-2
          class="drop-shadow-dark"
          height="30"
        />
      </button>
    </div>
    <div
      class="flex flex-wrap"
      :class="{ '-mr-2': !hasInfoSection }"
    >
      <div
        v-if="getThumbnails?.length > 1"
        class="w-12/12 px-0 mb-1 md:mb-0"
        :class="thumbnailBreakpoints"
      >
        <div class="table size-full">
          <div class="table-row info-carousel-thumbnails">
            <div
              v-for="(thumbnail, index) in getThumbnails"
              :key="index"
              class="table-cell relative"
            >
              <div class="size-full absolute pr-1 md:pr-2">
                <ResponsiveHotelImage
                  class="size-full"
                  :image="thumbnail"
                  :alt="`Slide thumbnail ${index + 1}`"
                  preferred-size="large"
                  fit="cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="hasInfoSection"
        class="w-12/12 bg-white-blue"
        :class="slotBreakpoints"
      >
        <slot />
      </div>
    </div>
    <LazyFullscreenImageCarousel
      v-if="canFullscreen && showModal"
      :images="images"
      :initial-slide="currentSlideIndex"
      :visible="showModal"
      @on-hide="hideFullscreenImageCarousel"
    />
  </div>
</template>

<script>
import screenWidthMixin from '@packages/screen/mixins/screen-width.mixin.js'
import swiperProps from '@packages/swiper/constants/props.js'

export default defineNuxtComponent({
  setup () {
    const { public: { images: imagesConfig } } = useRuntimeConfig()

    return {
      imagesConfig,
    }
  },

  mixins: [screenWidthMixin],

  props: {
    ...swiperProps,

    images: {
      type: Array,
      default: () => [],
    },

    allowFullscreen: {
      type: Boolean,
      default: true,
    },

    pagination: {
      type: Boolean,
      default: false,
    },

    arrowsType: {
      type: String,
      default: 'circular',
    },

    thumbnailAutoplaySpeed: {
      type: Number,
      default: 15000,
    },
  },

  data () {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const { images, thumbnails, allowFullscreen, ...mainCarouselProps } = this.$props

    return {
      showModal: false,
      currentSlideIndex: 0,
      mainCarouselProps,
      maxThumbnails: 3,
    }
  },

  computed: {
    hasInfoSection () {
      return this.$slots.default?.()
    },

    getImages () {
      if ((this.images || []).length === 0) {
        return [{
          src: this.imagesConfig.fallbackImage,
        }]
      }
      return this.images
        .filter(Boolean)
    },

    getThumbnails () {
      const startIndex = this.initialSlide + 1
      // Max 2 when it gets too tight
      const endIndex = this.isWider('2xl') || this.isNarrower('md')
        ? startIndex + this.maxThumbnails
        : startIndex + 2

      return this.getImages
        .slice(startIndex, endIndex)
    },

    isDefaultImage () {
      return this.getImages[0].src === this.imagesConfig.fallbackImage
    },

    canFullscreen () {
      return this.allowFullscreen && !this.isDefaultImage
    },

    thumbnailBreakpoints () {
      if (!this.hasInfoSection) {
        return 'grow'
      }

      return {
        'md:w-6/12': this.getThumbnails?.length === 3,
        'md:w-5/12': this.getThumbnails?.length === 2,
      }
    },

    slotBreakpoints () {
      return {
        'md:w-6/12': this.getThumbnails?.length === 3,
        'md:w-7/12': this.getThumbnails?.length === 2,
      }
    },
  },

  methods: {
    showFullscreenImageCarousel () {
      this.showModal = true
    },

    hideFullscreenImageCarousel () {
      this.showModal = false
    },

    handleSlideChange (_, newSlideIndex) {
      this.currentSlideIndex = newSlideIndex
    },
  },
})
</script>

<style lang="scss">
.info-carousel {
  &-main {
    height: 400px;
  }

  &-thumbnails {
    height: 150px;
  }
}
</style>
